import * as Logger from './logger.js'
import { Auth } from './auth.js'
import { evaluate, setSafeHtml } from './utils.js';
import styles from './component.css';
import template from './component.html';

export class HSComponent extends HTMLElement {
    #loaded;    // if DOM is loaded or not
    config = {};
    editMode = false;
    abortController = new AbortController();    // used to abort event listeners and any fetch activity

    constructor() { 
        super();
        this.#loaded = false;
        HSComponent.constructed(this);  // for lifecycle tracking
        this.attachShadow({ mode: 'open' });
    }

    onLoad() {
        // base class no-op
    }

    onClose() {
        // called BEFORE the component is removed from the DOM
    }

    onUnload() {
        // called AFTER the component is removed from the DOM, config is not available
        // a child class may override this to perform some cleanup, but needs to call super.onUnload()
        this.abort();   // remove all event handlers
        Logger.verbose("lifecycle counts: ", HSComponent.lifecycle_counts);
    }

    init(config) {
        this.config = config;
        // freeze the config object to prevent modifications, config changes 
        // should be stored as a copy so we can compare and save changes to 
        // the backend
        //Object.freeze(this.config);
    }

    //
    // methods to update editMode and ripple down through shadow DOM of children
    //
    toggleEditMode(enabled) {
        this.editMode = enabled;
        this.toggleChildrenEditMode(this.shadowRoot, enabled);
        this.setChildrenEditMode(this.shadowRoot, enabled);
    }
    toggleChildrenEditMode(node, enabled) {
        for (const child of node.children) {
            if (child instanceof HSComponent) {
                child.toggleEditMode(enabled);
            } else {
                this.toggleChildrenEditMode(child, enabled);
            }
        }
    }
    setChildrenEditMode(node, enabled) {
        for (const el of node.querySelectorAll(".edit-mode")) {
            if (enabled) {
                el.classList.add("edit-mode-enabled");
            } else {
                el.classList.remove("edit-mode-enabled");
            }
        }
    }

    loadStyles(styles) {
        const stylesheet = new CSSStyleSheet();
        stylesheet.replaceSync(styles);
        this.shadowRoot.adoptedStyleSheets.push(stylesheet);
        //this.shadowRoot.adoptedStyleSheets = [ this.styles ];
        /*
        this.styles.replace(styles)
          .then(sheet => {
            Logger.debug('Styles loaded successfully', sheet);
            this.shadowRoot.adoptedStyleSheets = [ sheet ];
          })
          .catch(err => {
            Logger.error('Failed to load css:', err);
          });
          */
    }

    setTemplate(template) {
        let el = this.shadowRoot.querySelector("template");
        if (el) {
            setSafeHtml(el, evaluate(template, this));
        } else {
            this.loadTemplate(template);
        }
    }

    loadTemplate(template) {
        const el = document.createElement('template');
        const html = evaluate(template, this);
        setSafeHtml(el, html);
        this.appendChild(el.content.cloneNode(true));
        return html;
    }

    clearTemplate() {
        while (this.shadowRoot.hasChildNodes()) {
            Logger.debug("removing child node: ", this.shadowRoot.firstChild);
            this.shadowRoot.removeChild(this.shadowRoot.firstChild);
        }
    }

    appendChild(el) {
        this.shadowRoot.appendChild(el);
    }

    removeChild(el) {
        this.shadowRoot.removeChild(el);
    }

    async fetch(path, { method = 'GET', payload = undefined, expectedStatuses = [] } = {}) {
        return await Auth.fetch({ path, method, payload, expectedStatuses });
    }

    apiUrl(path) {
        return Auth.apiUrl(path);
    }

    async put(path, jsonContent) {
        return await this.fetch(path, { method: 'PUT', payload: jsonContent });
    }

    showLoading() { 
        let loadingIcon = this.shadowRoot.getElementById("loading");
        if (!loadingIcon) {
            this.loadTemplate(template);
            this.loadStyles(styles);
            loadingIcon = this.shadowRoot.getElementById("loading");
        }
        loadingIcon.classList.remove("hide"); 
    }

    hideLoading() { 
        const loadingIcon = this.shadowRoot.getElementById("loading");
        if (loadingIcon) {
            loadingIcon.classList.add("hide"); 
        }
    }

    connectedCallback() {
        if (this.loaded) { 
            Logger.debug("ignoring onLoad, already loaded");
        } else {
            this.loaded = true;
            this.onLoad();
            HSComponent.lifecycle_counts[this.constructor.name].added++;
        }
    }

    disconnectedCallback() {
        this.onUnload();
        HSComponent.lifecycle_counts[this.constructor.name].removed++;
        this.abort();
    }

    // send an event to a parent component
    emit({ type, source = this, ...data }) {
        Logger.debug(`emitting event '${type}' from source: `, source);
        source.dispatchEvent(new CustomEvent(type, {
            bubbles: true,
            composed: true,
            cancelable: true,
            detail: { ...data }
        }));
    }

    setActionHandler(name, handler) {
        // method for setting action handlers; to be implemented by subclasses (e.g., HSCard)
    }

    // handle events from this or any child components
    onEvent(type, handler, target = this) {
        target.addEventListener(type, ev => handler(ev.detail, ev), { signal: this.abortController.signal });
    }

    // cancel any timers, close any sockets, and remove any event listeners to allow garbage collection to free memory
    abort() {
        this.abortController.abort();
        // once an AbortController is used, it can't be reset, so we allocate a new one
        // to be used for any subsequent activity
        this.abortController = new AbortController();
    }

    /*
    // TODO: we should convert this to use proper module style import, as we do for echarts, gridstack, slickgrid, jsoneditor, etc.
    // also, we prefer Flexbox - use that if possible instead of Bootstrap
    injectBootstrapCSS() {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css";
        this.shadowRoot.appendChild(link);
    }
    */

    findChildren({ predicate, shadowOnly = true, maxDepth = 0, node = this.shadowRoot, currentDepth = 0 } = {}) {
        const children = [];
        const recurse = (ref, depth) => {
            for (const child of ref.children) {
                if (predicate && predicate(child)) {
                    children.push(child);
                }
                if (maxDepth == 0 || depth < maxDepth) {
                    if (child instanceof HSComponent && !shadowOnly) {
                        // allow extending the search into the child component shadowRoot
                        children.push(...child.findChildren({ predicate, shadowOnly, maxDepth, currentDepth: depth + 1 }));
                    } else {
                        recurse(child, depth + 1);
                    }
                }
            }
        }
        recurse(node, 1);
        return children;
    }

    //
    // we keep track of the lifecycle counts of any objects derived from this base Component class:
    //  - how many components have been built, added to DOM, removed from DOM, and destroyed (per type)
    //  - this is stored as static data and can be used for memory leak detection and other issues
    static lifecycle_counts = {};
    static lifecycle_registry = null;
    static constructed(obj) {
        const type = obj.constructor.name;
        if (!this.lifecycle_counts[type]) {
            this.lifecycle_counts[type] = { created: 1, added: 0, removed: 0, destroyed: 0 };
        } else {
            this.lifecycle_counts[type].created++;
        }
        if (!this.lifecycle_registry) {
            this.lifecycle_registry = new FinalizationRegistry(childType => { this.lifecycle_counts[childType].destroyed++; });
        }
        this.lifecycle_registry.register(obj, type);
    }

    saveConfig() {
        return this.config;
    }

    configSaved() {
        // nothing to do, descendent classes may override this as needed
    }
}
